<template>
  <div class="cl-panel-stack">
    <article class="card">
      <header class="cl-card-head">
        <div>
          <p class="eyebrow">Compare</p>
          <h3>Two runs side by side</h3>
          <p class="muted-line">
            The run on the left is the one selected above. Differences are shown against it, so a positive figure means
            the other run did more of that thing.
          </p>
        </div>
        <div class="page-actions">
          <AppDropdown
            size="sm"
            label="Compare with"
            align="right"
            placeholder="Pick a run"
            empty-label="No other run has been analyzed yet"
            :model-value="compareKey"
            :options="runOptions"
            :disabled="!runOptions.length"
            @update:model-value="$emit('select', $event)"
          />
          <button
            v-if="compareKey"
            class="btn-icon-outline"
            type="button"
            aria-label="Clear the comparison"
            title="Clear the comparison"
            @click="$emit('select', '')"
          >
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
      </header>

      <p v-if="sameDungeonOnly && !runOptions.length" class="cl-empty-line">
        No other run of this dungeon has been analyzed yet.
      </p>

      <label v-if="otherDungeonCount" class="cl-switch">
        <input type="checkbox" :checked="!sameDungeonOnly" @change="sameDungeonOnly = !sameDungeonOnly">
        <span>Also list runs of other dungeons ({{ otherDungeonCount }})</span>
      </label>
    </article>

    <article v-if="loading" class="card cl-placeholder">
      <i class="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
      <h3>Loading the other run</h3>
    </article>

    <template v-else-if="other">
      <article class="card">
        <header class="cl-card-head is-tight">
          <div>
            <p class="eyebrow">Headline</p>
            <h3>{{ report.name }} vs {{ other.name }}</h3>
            <p class="muted-line">{{ basisNote }}</p>
          </div>
        </header>

        <div class="cl-compare-grid">
          <div class="cl-compare-row is-head">
            <span>Metric</span>
            <span>{{ leftLabel }}</span>
            <span>{{ rightLabel }}</span>
            <span>Difference</span>
          </div>
          <div v-for="row in headline" :key="row.key" class="cl-compare-row">
            <span class="cl-compare-metric">{{ row.label }}</span>
            <span class="cl-compare-value">{{ row.left }}</span>
            <span class="cl-compare-value">{{ row.right }}</span>
            <span class="cl-compare-delta" :class="row.deltaClass">{{ row.delta }}</span>
          </div>
        </div>
      </article>

      <article class="card">
        <header class="cl-card-head is-tight">
          <div>
            <p class="eyebrow">Players</p>
            <h3>Per player difference</h3>
            <p class="muted-line">
              Matched by character name. A player present in only one of the two runs is listed with no data on the
              other side.
            </p>
          </div>
        </header>

        <div class="cl-table-scroll">
          <table class="cl-table">
            <thead>
              <tr>
                <th>Player</th>
                <th class="is-numeric">DPS {{ leftShort }}</th>
                <th class="is-numeric">DPS {{ rightShort }}</th>
                <th class="is-numeric">Diff</th>
                <th class="is-numeric">HPS {{ leftShort }}</th>
                <th class="is-numeric">HPS {{ rightShort }}</th>
                <th class="is-numeric">Diff</th>
                <th class="is-numeric">Deaths</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in playerRows" :key="row.name">
                <td>
                  <strong class="cl-class-name" :style="row.color ? { color: row.color } : null">{{ row.name }}</strong>
                </td>
                <td class="is-numeric">{{ row.leftDps }}</td>
                <td class="is-numeric">{{ row.rightDps }}</td>
                <td class="is-numeric" :class="row.dpsClass">{{ row.dpsDelta }}</td>
                <td class="is-numeric">{{ row.leftHps }}</td>
                <td class="is-numeric">{{ row.rightHps }}</td>
                <td class="is-numeric" :class="row.hpsClass">{{ row.hpsDelta }}</td>
                <td class="is-numeric">{{ row.deaths }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </template>

    <article v-else class="card cl-placeholder">
      <i class="fa-solid fa-scale-balanced" aria-hidden="true"></i>
      <h3>Nothing to compare yet</h3>
      <p class="muted-line">
        Pick another run above. Only runs whose analysis is still cached can be opened without re-reading their log.
      </p>
    </article>
  </div>
</template>

<script>
// Side by side comparison of two runs.
//
// Both sides are read from reports that were already built, so nothing is
// re-parsed here. Rates are divided by each run's own duration, which is the
// only honest way to compare a 22 minute key with a 27 minute one, and the
// header says so.

import AppDropdown from '../AppDropdown.vue'
import {
  NO_DATA,
  formatCompact,
  formatDuration,
  formatClock,
  formatRate,
  plural,
} from '../../services/combatLog/format'

function rate(total, ms) {
  if (!ms) return 0
  return (total * 1000) / ms
}

function signed(value, format) {
  if (value === 0) return '0'
  return `${value > 0 ? '+' : '-'}${format(Math.abs(value))}`
}

// Green and red are read as good and bad, so they follow whether the second
// run did better, not merely whether the number went up: more damage taken is
// an increase and a regression at the same time.
function deltaClass(value, lowerIsBetter = false) {
  if (!value) return ''
  const better = lowerIsBetter ? value < 0 : value > 0
  return better ? 'is-better' : 'is-worse'
}

export default {
  name: 'CombatLogCompare',
  components: { AppDropdown },
  props: {
    report: { type: Object, required: true },
    summary: { type: Object, default: null },
    runs: { type: Array, default: () => [] },
    compareKey: { type: String, default: '' },
    other: { type: Object, default: null },
    loading: { type: Boolean, default: false },
    currentKey: { type: String, default: '' },
  },
  emits: ['select'],
  data() {
    return { NO_DATA, sameDungeonOnly: true }
  },
  computed: {
    // Comparing a key with a different dungeon is rarely what is meant, so the
    // list starts narrowed and says how many it is hiding.
    selectable() {
      return this.runs.filter((run) => run.key !== this.currentKey)
    },
    sameDungeon() {
      const name = this.report.name
      return this.selectable.filter((run) => run.name === name)
    },
    otherDungeonCount() {
      return this.selectable.length - this.sameDungeon.length
    },
    runOptions() {
      const pool = this.sameDungeonOnly ? this.sameDungeon : this.selectable
      return pool.map((run) => {
        const level = run.meta && run.meta.keystoneLevel ? ` +${run.meta.keystoneLevel}` : ''
        const result = run.meta && run.meta.success === true
          ? 'Kill'
          : (run.meta && run.meta.success === false ? 'Wipe' : '')
        return {
          value: run.key,
          label: `${run.name}${level}`,
          icon: run.type === 'challenge' ? 'fa-dungeon' : 'fa-dragon',
          hint: [
            run.startTs ? `${new Date(run.startTs).toLocaleDateString('ru-RU')} ${formatClock(run.startTs)}` : '',
            result,
            plural(run.deathCount, 'death'),
          ].filter(Boolean).join(' - '),
          meta: formatDuration(run.fightMs || run.durationMs),
        }
      })
    },
    leftLabel() {
      return this.describeSide(this.report)
    },
    rightLabel() {
      return this.describeSide(this.other)
    },
    leftShort() {
      return 'A'
    },
    rightShort() {
      return 'B'
    },
    basisNote() {
      return `A is ${this.leftLabel}, B is ${this.rightLabel}. Rates are divided by each run's own duration.`
    },
    headline() {
      const left = this.report
      const right = this.other
      const leftMs = left.basisMs || left.durationMs || 0
      const rightMs = right.basisMs || right.durationMs || 0

      const rows = [
        {
          key: 'duration',
          label: 'Duration',
          leftValue: leftMs,
          rightValue: rightMs,
          format: formatDuration,
          // A longer run is not an achievement, so the arrow is not coloured.
          neutral: true,
        },
        {
          key: 'dps',
          label: 'Group DPS',
          leftValue: rate(left.totals.damage, leftMs),
          rightValue: rate(right.totals.damage, rightMs),
          format: formatRate,
        },
        {
          key: 'damage',
          label: 'Damage',
          leftValue: left.totals.damage,
          rightValue: right.totals.damage,
          format: formatCompact,
        },
        {
          key: 'hps',
          label: 'Group HPS',
          leftValue: rate(left.totals.healing, leftMs),
          rightValue: rate(right.totals.healing, rightMs),
          format: formatRate,
        },
        {
          key: 'taken',
          label: 'Damage taken',
          leftValue: left.totals.damageTaken,
          rightValue: right.totals.damageTaken,
          format: formatCompact,
          lowerIsBetter: true,
        },
        {
          key: 'deaths',
          label: 'Deaths',
          leftValue: left.deaths.length,
          rightValue: right.deaths.length,
          format: (value) => String(Math.round(value)),
          lowerIsBetter: true,
        },
        {
          key: 'interrupts',
          label: 'Interrupts',
          leftValue: left.totals.interrupts,
          rightValue: right.totals.interrupts,
          format: (value) => String(Math.round(value)),
        },
      ]

      return rows.map((row) => {
        const diff = row.rightValue - row.leftValue
        return {
          key: row.key,
          label: row.label,
          left: row.format(row.leftValue),
          right: row.format(row.rightValue),
          delta: signed(diff, row.format),
          deltaClass: row.neutral ? '' : deltaClass(diff, row.lowerIsBetter),
        }
      })
    },
    playerRows() {
      const leftMs = this.report.basisMs || this.report.durationMs || 0
      const rightMs = this.other.basisMs || this.other.durationMs || 0
      const byName = new Map()

      const put = (player, side) => {
        const entry = byName.get(player.name) || { name: player.name, color: player.color, left: null, right: null }
        entry[side] = player
        if (!entry.color) entry.color = player.color
        byName.set(player.name, entry)
      }

      this.report.players.forEach((player) => put(player, 'left'))
      this.other.players.forEach((player) => put(player, 'right'))

      return [...byName.values()]
        .map((entry) => {
          const leftDps = entry.left ? rate(entry.left.damage, leftMs) : null
          const rightDps = entry.right ? rate(entry.right.damage, rightMs) : null
          const leftHps = entry.left ? rate(entry.left.healing, leftMs) : null
          const rightHps = entry.right ? rate(entry.right.healing, rightMs) : null
          const dpsDiff = leftDps !== null && rightDps !== null ? rightDps - leftDps : null
          const hpsDiff = leftHps !== null && rightHps !== null ? rightHps - leftHps : null

          return {
            name: entry.name,
            color: entry.color,
            sort: Math.max(leftDps || 0, rightDps || 0),
            leftDps: leftDps === null ? NO_DATA : formatRate(leftDps),
            rightDps: rightDps === null ? NO_DATA : formatRate(rightDps),
            dpsDelta: dpsDiff === null ? NO_DATA : signed(dpsDiff, formatRate),
            dpsClass: dpsDiff === null ? '' : deltaClass(dpsDiff),
            leftHps: leftHps === null ? NO_DATA : formatRate(leftHps),
            rightHps: rightHps === null ? NO_DATA : formatRate(rightHps),
            hpsDelta: hpsDiff === null ? NO_DATA : signed(hpsDiff, formatRate),
            hpsClass: hpsDiff === null ? '' : deltaClass(hpsDiff),
            deaths: `${entry.left ? entry.left.deaths : '-'} / ${entry.right ? entry.right.deaths : '-'}`,
          }
        })
        .sort((left, right) => right.sort - left.sort)
    },
  },
  methods: {
    describeSide(report) {
      if (!report) return NO_DATA
      const level = report.meta && report.meta.keystoneLevel ? ` +${report.meta.keystoneLevel}` : ''
      const date = report.startTs ? new Date(report.startTs).toLocaleDateString('ru-RU') : ''
      return `${report.name}${level}${date ? ` (${date})` : ''}`
    },
  },
}
</script>
